/**
 * « Suivre cette liste » et « Reprendre La Synthèse du soir » : la liste du soir enregistrée
 * dans Mes grilles par POST /_api/auth/grilles/depuis-liste (MesGrillesController). Le
 * navigateur n'envoie que le nom de la liste ; ses numéros sont lus par le serveur.
 */
export const LISTES_SUIVIES = {
  forme: "La Forme",
  annonces: "Les Absents",
  synthese: "La Synthèse",
  records: "Proches de leur record",
  tops: "Tops historiques",
} as const;

export type ListeSuivie = keyof typeof LISTES_SUIVIES;

export type GrilleEnregistree = { id: number; name: string; numbers: number[] };

export type ResultatSuivi = {
  /** deja : mêmes numéros déjà dans Mes grilles (double clic), rien d'écrit. */
  statut: "creee" | "remplacee" | "deja" | "quota" | "invalide";
  grille?: GrilleEnregistree;
  /** Nom de la version précédente de la même liste, remplacée par celle du soir. */
  remplace?: string;
  max?: number;
  remplacable?: boolean;
  grilleActuelle?: GrilleEnregistree | null;
  error?: string;
};

export const estListeSuivie = (v: unknown): v is ListeSuivie =>
  typeof v === "string" && Object.prototype.hasOwnProperty.call(LISTES_SUIVIES, v);

export async function suivreListe(
  liste: ListeSuivie,
  options: { remplacer?: boolean; source?: string } = {},
  fetchFn: typeof fetch = fetch,
): Promise<ResultatSuivi> {
  try {
    const r = await fetchFn("/_api/auth/grilles/depuis-liste", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ liste, remplacer: options.remplacer === true, source: options.source ?? "pronostics_suivre" }),
    });
    const data = (await r.json().catch(() => ({}))) as Partial<ResultatSuivi>;
    if (data && typeof data.statut === "string") return data as ResultatSuivi;
    return { statut: "invalide", error: data?.error };
  } catch {
    return { statut: "invalide", error: "Connexion impossible, réessayez." };
  }
}

/** Arrivée dans Mes grilles : confirmation, ou proposition de remplacer la grille suivie. */
export function arriveeApresSuivi(r: ResultatSuivi, liste: ListeSuivie): string {
  if ((r.statut === "creee" || r.statut === "remplacee" || r.statut === "deja") && r.grille) {
    const p = new URLSearchParams({ enregistree: String(r.grille.id) });
    if (r.remplace) p.set("remplace", r.remplace);
    return `/mon-keno/?${p.toString()}`;
  }
  if (r.statut === "quota") return `/mon-keno/?suivre=${liste}`;
  return "/mon-keno/";
}

/** Grille au hasard (premier écran) : numéros distincts de 1 à 56, triés. */
export function grilleAuHasard(taille = 10, alea: () => number = Math.random): number[] {
  const restants = Array.from({ length: 56 }, (_, i) => i + 1);
  const choisis: number[] = [];
  while (choisis.length < taille && restants.length > 0) {
    choisis.push(restants.splice(Math.floor(alea() * restants.length), 1)[0]);
  }
  return choisis.sort((a, b) => a - b);
}

/** Premier score d'une grille enregistrée maintenant : tirage de 20 h, heure de Paris. */
export function premierScore(now: Date = new Date()): "ce soir" | "demain soir" {
  const parts = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "numeric", hour12: false }).formatToParts(now);
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  return h < 20 ? "ce soir" : "demain soir";
}

export type IntentionGrille = { liste: ListeSuivie } | { numeros: number[] };

/**
 * Grille à enregistrer après l'inscription, lue dans l'adresse de /inscription/ (?liste= d'un
 * bouton « Suivre cette liste », ?numbers= du widget « Testez vos numéros »). Envoyée au serveur
 * avec la demande de lien magique ou le départ Google, qui la contrôle de nouveau (GrilleIntent).
 */
export function intentionDepuisAdresse(params: URLSearchParams): IntentionGrille | null {
  const liste = params.get("liste");
  if (liste !== null) return estListeSuivie(liste) ? { liste } : null;
  const brut = params.get("numbers");
  if (!brut) return null;
  const numeros = brut.split(",").map((n) => (/^\d{1,2}$/.test(n.trim()) ? Number(n.trim()) : NaN));
  const valides = numeros.every((n) => Number.isInteger(n) && n >= 1 && n <= 56) && new Set(numeros).size === numeros.length;
  return valides && numeros.length >= 4 && numeros.length <= 10 ? { numeros } : null;
}
